import { AxiosError } from 'axios';
import { types, flow, Instance } from 'mobx-state-tree';
import axios from 'utils/axios.config';
import { withAlertStore } from 'components/Core/CustomAlert/store';
import { ErrorData, IResponse, StatusTypeReq } from 'types/common';
import { IPopularProductData } from './types';
import { IPopularProductForm } from 'components/Forms/PopularProductForm/types';

const { model, frozen, maybeNull } = types;

export const PopularProductsStore = model('PopularProductsStore', {
  popularProductsList: maybeNull(frozen<IPopularProductData[]>()),
  popularProductData: maybeNull(frozen<IPopularProductData>()),
})
  .extend(withAlertStore)
  .actions((self) => ({
    setPopularProductsList(data: IPopularProductData[] | null) {
      self.popularProductsList = data;
    },
    setPopularProductData(data: IPopularProductData | null) {
      self.popularProductData = data;
    },
  }))
  .actions((self) => ({
    errorHandling({ response }: AxiosError<IResponse<ErrorData>>) {
      const msg = response?.data.message || 'Internal server error';
      self.alertStore.showAlert(msg);
      throw new Error(msg);
    },
  }))
  .actions((self) => ({
    getPopularProductsList: flow(function* () {
      yield axios
        .get('popular-products')
        .then(({ data: { data } }) => self.setPopularProductsList(data))
        .catch(self.errorHandling);
    }),
    changePopularProductsStatus: flow(function* ({ id, status }: StatusTypeReq) {
      yield axios
        .patch(`popular-products/${id}/status`, { status })
        .then(({ data: { data } }) => {
          self.setPopularProductsList(data);
        })
        .catch(self.errorHandling);
      return id;
    }),
    getPopularProductData: flow(function* (id: string) {
      yield axios
        .get(`popular-products/${id}`)
        .then(({ data: { data } }) => self.setPopularProductData(data))
        .catch(self.errorHandling);
    }),
    addPopularProduct: flow(function* (data: IPopularProductForm) {
      return yield axios
        .post(`popular-products`, data)
        .then(({ data: { data } }) => data)
        .catch(self.errorHandling);
    }),
    editPopularProduct: flow(function* (data: IPopularProductForm) {
      yield axios.patch(`popular-products/${data.id}`, data).catch(self.errorHandling);
      return null;
    }),
    deletePopularProduct: flow(function* (id: string) {
      yield axios.delete(`popular-products/${id}`).catch(self.errorHandling);
      return null;
    }),
    changePopularProductPositions: flow(function* (data: IPopularProductData[]) {
      yield axios
        .patch(`popular-products/position`, { products: data })
        .then(({ data: { data } }) => self.setPopularProductsList(data))
        .catch(self.errorHandling);
    }),
  }));

export type IPopularProductsStore = Instance<typeof PopularProductsStore>;
